import Button from "@material-ui/core/Button/Button";
import Card from "@material-ui/core/Card/Card";
import CardContent from "@material-ui/core/CardContent/CardContent";
import Divider from "@material-ui/core/Divider/Divider";
import Grid from "@material-ui/core/Grid/Grid";
import IconButton from "@material-ui/core/IconButton/IconButton";
import Typography from "@material-ui/core/Typography/Typography";
import PrintIcon from "@material-ui/icons/Print";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CartSummary } from "@common/cart/CartSummary";
import { clearStorage, getProspectId, getIsAnonymous } from "@utils/storage";
import "./css/confirmationPage.scss";
import { useFetch } from "@common/hooks/useFetch";
import { finaliseRouteCrossSell, FINALIZE_ORDER_ROUTE } from "@api-routes";
import { useGetCart } from "@hooks/useGetCart";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { useHistory } from "react-router-dom";
import { ROUTES } from "@constants";
import { fixUrlLanguage } from "@utils/changeLanguage";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";

interface IFinalizeOrderResponse {
    success: boolean;
    orderNumber: string;
    paymentRef: string;
    file: {
        name: string;
        base64File: string;
    };
    prospectId: string;
}

export const ConfirmationPage = () => {
    const { t } = useTranslation("confirmation");
    const isCrossSell = keycloakUtils.isAuthenticated();
    const { push } = useHistory();
    const [_, { post }] = useFetch();
    const { userCart } = useGetCart();
    const [finalizeOrderResponse, setFinalizeOrderResponse] = useState<IFinalizeOrderResponse>();

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        window.history.replaceState({}, document.title, window.location.href.split("?")[0]);

        const checkProspectId = () => {
            const prospectId = getProspectId();
            if (!prospectId) push(fixUrlLanguage(ROUTES.HOME_PAGE_URL_EN));
        };

        const getReceiptBinary = async () => {
            const orderId = searchParams.get("orderId");

            if (orderId)
                await post<IFinalizeOrderResponse>(
                    isCrossSell ? finaliseRouteCrossSell(getIsAnonymous()) : FINALIZE_ORDER_ROUTE,
                    {
                        body: {
                            orderId: orderId,
                        },
                        isSecured: keycloakUtils.isAuthenticated(),
                    }
                ).then((response) => {
                    setFinalizeOrderResponse(response);
                    if (response && response.success) {
                        clearStorage();
                    }
                });
        };

        checkProspectId();
        getReceiptBinary();
    }, []);

    const backToHome = () => {
        push(fixUrlLanguage(ROUTES.HOME_PAGE_URL_EN));
    };

    const retryPayment = () => {
        push(fixUrlLanguage(ROUTES.SUMMARY_PAGE_URL_EN));
    };

    const b64toBlob = (b64Data: any, contentType: string, sliceSize: number) => {
        contentType = contentType || "image/png";
        sliceSize = sliceSize || 512;

        var byteCharacters = atob(b64Data);
        var byteArrays = [];

        for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            var slice = byteCharacters.slice(offset, offset + sliceSize);

            var byteNumbers = new Array(slice.length);
            for (var i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            var byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        var blob = new Blob(byteArrays, { type: contentType });
        return blob;
    };

    const downloadFile = () => {
        var downloadLink = document.createElement("a");
        downloadLink.target = "_blank";
        downloadLink.download = finalizeOrderResponse?.file.name || "PaymentReceipt.pdf";

        // create an object URL from the Blob
        var URL = window.URL || window.webkitURL;
        // convert base64 data to a Blob
        var downloadUrl = URL.createObjectURL(
            b64toBlob(finalizeOrderResponse?.file.base64File, "application/pdf", 512)
        );

        // set object URL as the anchor's href
        downloadLink.href = downloadUrl;

        // append the anchor to document body
        document.body.append(downloadLink);

        // fire a click event on the anchor
        downloadLink.click();

        // cleanup: remove element and revoke object URL
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadUrl);
    };

    return (
        <div className="container-boxed">
            <Grid container spacing={8} wrap="wrap-reverse">
                <HandleErrorAndLoading error={_.error} isLoading={_.isLoading}>
                    <Grid item sm={6} xs={12}>
                        <Card elevation={2}>
                            <CartSummary editable={false} userCart={userCart} />
                        </Card>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <Card elevation={2}>
                            <CardContent className="confirmation-order__card">
                                <Typography align="center" color="inherit" variant="h3">
                                    {finalizeOrderResponse?.success ? t("orderConfirmation") : t("orderFailed")}
                                </Typography>
                            </CardContent>
                            {finalizeOrderResponse && (
                                <>
                                    {finalizeOrderResponse.success ? (
                                        <>
                                            <CardContent>
                                                <Typography color="secondary" variant="subtitle1">
                                                    {t("congrats")}
                                                </Typography>
                                                <Typography color="secondary" variant="subtitle1">
                                                    {t("workingOrder")}
                                                </Typography>
                                                <Grid container direction="row">
                                                    <Grid item xs={11}>
                                                        <Typography color="secondary" variant="subtitle1">
                                                            {t("orderNumber")}: {finalizeOrderResponse?.orderNumber}
                                                        </Typography>
                                                        <Typography color="secondary" variant="subtitle1">
                                                            {t("accountNumber")}: {finalizeOrderResponse?.prospectId}
                                                        </Typography>
                                                        <Typography color="secondary" variant="subtitle1">
                                                            {t("paymentRef")}: {finalizeOrderResponse?.paymentRef}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <IconButton
                                                            aria-label="Print"
                                                            className="printIcon"
                                                            onClick={() => {
                                                                if (finalizeOrderResponse?.success) downloadFile();
                                                            }}
                                                        >
                                                            <PrintIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                            <Divider />
                                            <CardContent>
                                                <Grid container spacing={8}>
                                                    <Grid item xs={12}>
                                                        <Typography align="center" color="secondary" variant="body1">
                                                            {t("deliveryDetails")}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                            <Divider />
                                            <CardContent>
                                                <Grid container justify="center" spacing={2}>
                                                    <Grid item xs={12}>
                                                        <Typography align="center" color="primary" variant="h6">
                                                            {t("enjoy")}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item md={7} xs={9}>
                                                        <Button
                                                            color="primary"
                                                            fullWidth
                                                            size="large"
                                                            variant="contained"
                                                            onClick={backToHome}
                                                        >
                                                            {t("backHome")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </>
                                    ) : (
                                        <>
                                            <CardContent>
                                                <Grid container justify="center" spacing={5}>
                                                    <Grid item xs={12}>
                                                        <Typography color="inherit" variant="body1">
                                                            {t("paymentFailed")}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid item md={7} xs={9} justify="center">
                                                        <Button
                                                            color="primary"
                                                            fullWidth
                                                            size="large"
                                                            variant="contained"
                                                            onClick={retryPayment}
                                                        >
                                                            {t("retry")}
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </>
                                    )}
                                </>
                            )}
                        </Card>
                    </Grid>
                </HandleErrorAndLoading>
            </Grid>
        </div>
    );
};
